import React, { useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useFormikContext, Field } from "formik";
import useServerText from "util/serverText";
import { DefaultLabel } from "components/common";
import { Col, Row } from "common/components";
import { FormikSelect, FormikInput, FormikMarkdownEditor } from "security-app/components/common/FormikComponents";
import { useGetPriorities, useGetStatuses } from "security-app/hooks/useInvestigationsAPI";
import { useUsersAndTeamsOptions } from "../hooks/useUsersAndTeamsOptions";
function FormFields() {
  const { t } = useTranslation("enterprise_security_investigations");
  const serverText = useServerText();
  const { values, errors, touched, setFieldValue } = useFormikContext();
  const assignToOptions = useUsersAndTeamsOptions();
  const { priorities } = useGetPriorities();
  const priorityOptions = useMemo(
    () => priorities.map((priority) => ({
      value: priority.priority,
      label: serverText(priority.text),
      default: priority.default
    })).sort((a, b) => b.value - a.value),
    [priorities, serverText]
  );
  const { statuses } = useGetStatuses();
  const statusOptions = useMemo(
    () => statuses.map((status) => ({
      value: status.status,
      label: serverText(status.status),
      default: status.default
    })),
    [statuses, serverText]
  );
  useEffect(() => {
    const defaultStatus = statusOptions.find((option) => option.default);
    if (!values.status && defaultStatus) setFieldValue("status", defaultStatus.value);
  }, [setFieldValue, statusOptions, values.status]);
  const renderOption = (option) => /* @__PURE__ */ React.createElement("span", { key: option.value, title: option.value }, option.label, option.default && /* @__PURE__ */ React.createElement(DefaultLabel, null));
  return /* @__PURE__ */ React.createElement(Col, null, /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(
    Field,
    {
      component: FormikInput,
      name: "name",
      id: "name",
      type: "text",
      label: t("form_fields.name_label"),
      placeholder: t("form_fields.name_placeholder"),
      value: values.name,
      required: true,
      error: touched.name && errors.name ? errors.name : void 0,
      bsStyle: touched.name && errors.name ? "error" : null
    }
  )), /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(
    Field,
    {
      component: FormikSelect,
      name: "assigned_to",
      id: "assigned_to",
      placeholder: t("form_fields.assign_to_placeholder"),
      label: t("form_fields.assign_to_label"),
      value: values.assigned_to,
      options: assignToOptions,
      maxMenuHeight: 200,
      menuPlacement: "top"
    }
  )), /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(
    Field,
    {
      component: FormikSelect,
      name: "priority",
      id: "priority",
      placeholder: t("form_fields.priority_placeholder"),
      label: t("form_fields.priority_label"),
      value: values.priority,
      options: priorityOptions,
      maxMenuHeight: 200,
      menuPlacement: "top",
      optionRenderer: renderOption
    }
  ), /* @__PURE__ */ React.createElement(
    Field,
    {
      component: FormikSelect,
      name: "status",
      id: "status",
      placeholder: t("form_fields.status_placeholder"),
      label: t("form_fields.status_label"),
      value: values.status,
      options: statusOptions,
      maxMenuHeight: 200,
      menuPlacement: "top",
      optionRenderer: renderOption
    }
  )), /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(
    Field,
    {
      id: "notes",
      name: "notes",
      component: FormikMarkdownEditor,
      label: t("form_fields.notes_label"),
      height: 150
    }
  )));
}
export default FormFields;
